import polyglotI18nProvider from "ra-i18n-polyglot";
import de from "ra-language-german";
import {
  ASFCourseSessionType,
  isTheoryLesson,
  type TheoryLesson,
  type VehicleEventType,
} from "./model/autovioCalendarEvents";
import { type TheoryLecture } from "./model/TheoryLecture";
import { type VehicleType } from "./model/VehicleType";
import { type CustomizationFlag } from "./model/customizationFlags";

de.ra.action.search = "Suche";
de.ra.action.add_filter = "Filter";
de.ra.action.remove_all_filters = "Aktuellen Filter zurücksetzen";
de.ra.action.select_columns = "Spalten";
de.ra.action.clear_array_input = "Liste löschen";
de.ra.message.clear_array_input = "Wirklich alle Einträge löschen?";
de.ra.page.empty = "Noch keine %{name}.";
de.ra.saved_queries = {
  label: "Gespeicherte Filter",
  query_name: "Name des Filters",
  new_label: "Aktuellen Filter speichern ...",
  new_dialog_title: "Aktuellen Filter speichern als",
  remove_label: "Gespeicherten Filter löschen",
  remove_label_with_name: 'Filter "%{name}" löschen',
  remove_dialog_title: "Filter löschen?",
  remove_message: "Sind Sie sicher, dass Sie den gespeichert Filter löschen möchten?",
  help: "Filtern Sie die Liste und speichern Sie Filter für später",
};

export const i18nProvider = polyglotI18nProvider(
  /* getMessages: */ () => de,
  /* initialLocale: */ "de",
  /* availableLocales: */ [{ locale: "de", name: "Deutsch" }],
  /* polyglotOptions: */ { allowMissing: true },
);

export const l10n = {
  lectureXxx(x: TheoryLesson | TheoryLecture): string {
    const { category, lectureNumber } = isTheoryLesson(x) ? x.theoryLecture : x;
    switch (category) {
      case "Grundstoff":
        return `Lektion\u00A0${lectureNumber}`;
      case "Zusatzstoff_A":
        return `Lektion\u00A0A${lectureNumber}`;
      case "Zusatzstoff_B":
        return `Lektion\u00A0B${lectureNumber}\u00A0(${12 + lectureNumber})`;
      case "B96":
        return "Theorie\u00A0Klasse\u00A0B96";
      case "Zusatzstoff_L":
        return `Lektion\u00A0L${lectureNumber}`;
      case "Zusatzstoff_T":
        return `Lektion\u00A0T${lectureNumber}`;
      default:
        return "Lektion\u00A0???";
    }
  },
  asfCourseSessionTitle(sessionType: ASFCourseSessionType): string {
    switch (sessionType) {
      case "sitzung1":
        return "Gruppensitzung 1";
      case "fahrprobe":
        return "Fahrprobe (Blocker)";
      case "sitzung2":
        return "Gruppensitzung 2";
      case "sitzung3":
        return "Gruppensitzung 3";
      case "sitzung4":
        return "Gruppensitzung 4";
      default:
        return "Unbekannte Sitzung";
    }
  },
  vehicleTypePlural: {
    car: "PKWs",
    motorcycle: "Motorräder",
    trailer: "Anhänger",
    simulator: "Simulatoren",
    truck: "LKWs",
    bus: "Busse",
    tractor: "Traktoren",
  } satisfies Record<VehicleType, string>,
  vehicleEventType: {
    maintenance: "Wartung",
    charging: "Laden",
    blocking: "Blockiert",
  } satisfies Record<VehicleEventType, string>,
  customizationFlagLabel: {
    asfCourses: "ASF-Kurse",
    bkfCourses: "BKF-Kurse",
    autovioSupportCenter: "Fahrschüler können den Support-Bereich in der AUTOVIO App benutzen",
    instructorSupportCenter: "Fahrlehrer können den Support-Bereich in der AUTOVIO App benutzen",
    carSharing: "Car Sharing",
    paymentAndInvoicing: "Payment & Invoicing",
    reports: "Fahrlehrer können Reports in der AUTOVIO-App einsehen",
    studentAdministrativeOnboarding: "Student Administrative Onboarding",
    simulatorDrivingLessons: "Fahrschüler können Simulator-Fahrstunden buchen",
    zoomMeetings: "Zoom Meetings",
    theoryLearning: "Fahrschüler können die Theorie mit der AUTOVIO-App lernen",
    skipCreditCheckForInvites: "Fahrlehrer können Fahrschüler ohne Guthaben-Check zu Fahrstunden einladen",
    pickupService: "Pickup Service",
    pickupFromHomeAddress: "Pickup From Home Address",
    cancellationPolicyPerLesson: "",
    cancellationPolicyPerTheoryAppointment: "Stornierungsbedingungen pro Theorietermin",
    cancellationPolicyPerPracticalAppointment: "Stornierungsbedingungen pro Praxistermin",
    cancellationPolicyPerSimulatorAppointment: "Stornierungsbedingungen pro Simulatortermin",
    newStudentsCanBookTheoryLessonsInAllBranches: "Neue Fahrschüler können Theorieunterricht in allen Filialen buchen",
    newStudentsCanBookDrivingLessons: "Neue Fahrschüler können Fahrstunden buchen",
    referralProgram: "Empfehlungsprogramm",
    canAddGiftedCredits: "Darf Guthaben verschenken (100% Fahrschule/0% AUTOVIO)",
    canManageStripeIBAN: "Kann Stripe IBAN-Daten einsehen und erstellen",
    studentsMustSignDocuments: "Fahrschüler müssen Dokumente unterschreiben",
    supportForTheEmploymentAgency: "Support für Agentur für Arbeit",
    nextGenerationInvoicing: "Neue Generation der Rechnungsstellung",
    dunning: "Mahnwesen",
    studentsCanEditOnboardingChecklist: "Fahrschüler können Onboarding Checkliste editieren",
    studentsCanBookTheoryLessons: "Fahrschüler können Theorie buchen",
    studentsMustAttendEveryTheoryLecture: "Fahrschüler müssen alle Theorie-Lektionen besuchen",
    chargeExamAuthorityFees: "Prüfungsgebühren automatisch einziehen",
    canCreateStudents: "Fahrschulpersonal kann Fahrschüler im Backoffice erstellen",
    subscriptionPackages: "AUTOVIO Pro in Angeboten zeigen",
    autoCreateAusbildungsnachweis: "Ausbildungsnachweis automatisch nach bestandener Prüfung erstellen",
    autoCreateSchaltnachweis: "Schaltnachweis automatisch nach Testfahrt Schaltkompetenz erstellen",
    trucks: "LKW-Führerschein (Klassen C, C1, CE und C1E)",
    buses: "Bus-Führerschein (Klassen D, D1, DE und D1E)",
    tractors: "Traktor-Führerschein (Klassen L und T)",
    canUseExternalBankTransfer: "Fahrschule kann externe Banküberweisungen als Zahlungsart anbieten",
    useUseStripeForB2BPayments: "Fahrschule kann Stripe als Zahlungsanbieter für B2B-Zahlungen nutzen",
  } satisfies Record<CustomizationFlag, string>,
};
